import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { Box, IconButton } from '@mui/material';
import type { NodeKey } from 'lexical';
import { $getNodeByKey } from 'lexical';
import type { KeyboardEvent, MouseEvent } from 'react';
import React, { useCallback, useContext } from 'react';
import { UserContext } from '../auth/UserContext';
import { Icons } from '../icons/Icons';
import { getAttachmentReferenceDownloadUrl } from './attachmentReferenceDownloadUtils';
import type { AttachmentReferenceData } from './attachmentReferenceUtils';
import { isSpaceKey } from './attachmentReferenceUtils';
import {
    MATERIAL_REFERENCE_ATTRIBUTE,
    MATERIAL_REFERENCE_CLASS,
    MATERIAL_REFERENCE_ID_ATTRIBUTE,
    MATERIAL_REFERENCE_LABEL_CLASS,
    MATERIAL_REFERENCE_LABEL_ATTRIBUTE,
    MATERIAL_REFERENCE_TYPE_ATTRIBUTE
} from './lexicalConstants';

type AttachmentReferenceComponentProps = AttachmentReferenceData & {
    nodeKey: NodeKey;
};

export default function AttachmentReferenceComponent({
    nodeKey,
    referenceId,
    referenceType,
    label
}: AttachmentReferenceComponentProps) {
    const [editor] = useLexicalComposerContext();
    const isEditable = useLexicalEditable();
    const { activeOrganizationAccount } = useContext(UserContext);

    const handleOpen = useCallback(
        async (event: MouseEvent | KeyboardEvent) => {
            event.preventDefault();
            event.stopPropagation();

            if (referenceId) {
                try {
                    const attachmentUrl = await getAttachmentReferenceDownloadUrl(referenceId, activeOrganizationAccount);
                    if (attachmentUrl) {
                        window.open(attachmentUrl, '_blank', 'noopener,noreferrer');
                    }
                } catch (error) {
                    // TODO: Replace this with a user-facing toast once the failure UX is finalized.
                    console.error('Failed to open attachment reference', error);
                }
            }
        },
        [activeOrganizationAccount, referenceId]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                handleOpen(event);
                return;
            }

            if (isSpaceKey(event.key)) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [handleOpen]
    );

    const handleKeyUp = useCallback(
        (event: KeyboardEvent) => {
            if (isSpaceKey(event.key)) {
                handleOpen(event);
            }
        },
        [handleOpen]
    );

    const handleRemove = useCallback(
        (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            editor.update(() => {
                $getNodeByKey(nodeKey)?.remove();
            });
        },
        [editor, nodeKey]
    );

    return (
        <Box
            component="span"
            className={MATERIAL_REFERENCE_CLASS}
            contentEditable={false}
            {...{
                [MATERIAL_REFERENCE_ATTRIBUTE]: 'true',
                [MATERIAL_REFERENCE_ID_ATTRIBUTE]: referenceId,
                [MATERIAL_REFERENCE_TYPE_ATTRIBUTE]: referenceType,
                [MATERIAL_REFERENCE_LABEL_ATTRIBUTE]: label
            }}
            onClick={handleOpen}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            role="button"
            tabIndex={0}
            aria-label={`Open ${label}`}
        >
            <Box component="span" className={MATERIAL_REFERENCE_LABEL_CLASS}>
                {label}
            </Box>
            {isEditable ? (
                <IconButton
                    size="small"
                    aria-label={`Remove ${label}`}
                    onClick={handleRemove}
                    sx={{
                        color: 'inherit',
                        height: 16,
                        ml: 0.25,
                        p: 0,
                        width: 16
                    }}
                >
                    <Icons.Close sx={{ fontSize: 12 }} />
                </IconButton>
            ) : null}
        </Box>
    );
}
